import {
  AnyTicket,
  DRAFT_LEG_ADAPTERS,
  IDraftLegAdapter,
} from "@/components/widgets/tradeFromScratch/helper/draftLegAdapter";
import { returnHighlightedKeys } from "@/components/widgets/tradeFromScratch/helper/useTicketShell";
import draftLegsStore from "@/store/draftLegsStore";
import tradeFromScratchStore, {
  TicketInstrument,
  returnTicketForInstrument,
  updateTicketForInstrument,
} from "@/store/tradeFromScratchStore";
import {
  IAllocationsPending,
  IBlockEditBaseline,
  IEditTicket,
  isEditCheckedKey,
  returnAllocationsPending,
  returnPendingEditKeys,
} from "@/components/widgets/tradeFromScratch/helper/blockEditHelper";

// LQC-2941 - a Trade Creation grid row standing for an unsaved leg. Its fields live in
// tradeFromScratchStore under `id`, so the row object itself never changes.
export interface IDraftLegRow {
  id: string;
  rowType: "draft";
  instrument: TicketInstrument;
  widgetId: string;
  // LQC-3027 - present when the draft is an edit of a saved block.
  edit?: IBlockEditBaseline;
}

export function isDraftLegRow(data: unknown): data is IDraftLegRow {
  return (data as IDraftLegRow | undefined)?.rowType === "draft";
}

// The draft's ticket, subscribed so every cell stays live, plus its adapter and updater.
export function useDraftLegTicket(row: IDraftLegRow) {
  const ticket = tradeFromScratchStore((state) =>
    returnTicketForInstrument(state, row.instrument, row.id),
  ) as AnyTicket | undefined;
  return {
    ticket,
    adapter: DRAFT_LEG_ADAPTERS[row.instrument],
    update: (changes: object) =>
      updateTicketForInstrument(row.id, row.instrument, changes),
  };
}

// LQC-3027 - an edit only checks what it can send; the rest is the saved block's.
export function returnDraftLegMissingEntries(
  row: IDraftLegRow,
  ticket: AnyTicket,
  adapter: IDraftLegAdapter,
): { key: string; label: string }[] {
  const entries = adapter.returnMissingEntries(ticket);
  const { edit } = row;
  return edit ? entries.filter(({ key }) => isEditCheckedKey(edit, key)) : entries;
}

export function returnDraftLegBlockingReasons(
  row: IDraftLegRow,
  ticket: AnyTicket,
  adapter: IDraftLegAdapter,
): string[] {
  if (!row.edit) return adapter.returnBlockingReasons(ticket);
  return returnDraftLegMissingEntries(row, ticket, adapter).map(({ label }) => label);
}

// Missing field keys to show red while this draft's blocked Save is hovered. Shared by
// the row cells and the detail, so both light up together.
export function useDraftLegHighlightedKeys(row: IDraftLegRow): Set<string> {
  const { ticket, adapter } = useDraftLegTicket(row);
  const isHoveringSave = draftLegsStore((state) => state.hoveredSaveDraftId === row.id);
  if (!ticket) return new Set();
  return returnHighlightedKeys(
    isHoveringSave,
    returnDraftLegBlockingReasons(row, ticket, adapter).length === 0,
    returnDraftLegMissingEntries(row, ticket, adapter),
  );
}

const NO_PENDING_KEYS: Set<string> = new Set();

// LQC-3027 - an edit draft's fields that differ from the saved block (shown orange until
// Saved). A new leg has nothing saved to differ from.
export function useDraftLegPendingKeys(row: IDraftLegRow): Set<string> {
  const { ticket } = useDraftLegTicket(row);
  if (!row.edit || !ticket) return NO_PENDING_KEYS;
  return returnPendingEditKeys(row.edit, ticket as IEditTicket);
}

// LQC-3029 - the allocation fields an edit changed, row by row; undefined on a new leg.
export function useDraftLegAllocationsPending(row: IDraftLegRow): IAllocationsPending | undefined {
  const { ticket } = useDraftLegTicket(row);
  if (!row.edit || !ticket) return undefined;
  return returnAllocationsPending(row.edit, ticket as IEditTicket);
}
